import { useEffect, useState } from 'react';
import { z } from 'zod';

import { supabase } from '../lib/supabase.js';

// ---------------------------------------------------------------------------
// "Is there any unread *activity* for this game?" - the signal that
// drives the sidebar Feedback dot.
//
// Activity here mirrors the activity panel's definition (use-feedback-
// inbox.ts hasRecentActivity): a group counts as active when something
// happened to it after its head was posted - reactions, replies, or
// thread follow-ups. A brand-new top-level message is not "activity";
// it shows in the chronological feed but does not bump the dot.
//
// Sources tracked:
//   1. feedback_reaction.observed_at  (any reaction on a row in this game)
//   2. feedback_pool.posted_at WHERE reply_to_external_id IS NOT NULL
//      OR (thread_id IS NOT NULL AND external_id != thread_id)
//      (replies + thread follow-ups, excluding the thread-creating head)
//   3. feedback_pool.posted_at WHERE signal_flags->>hasPoll = 'true'
//      (poll creation - a brand-new poll-bearing message is activity
//      because the polls feed surfaces it the same way the activity
//      panel does)
//   4. feedback_poll_vote.observed_at  (any vote in this game's polls)
//   5. feedback_poll.updated_at  (poll state changes, including
//      finalisation flips and the bot's MessageUpdate vote-tally
//      refresh path)
//   6. feedback_form_submissions.submitted_at  (any new form
//      submission in this game - matches the inbox's form-submission
//      activity entry)
//
// The hook holds the max across all six. The flag is true when that
// max is past lastReadAt.
// ---------------------------------------------------------------------------

const ReactionRowSchema = z.object({
  observed_at: z.string(),
});

const PostRowSchema = z.object({
  posted_at: z.string().nullable(),
});

const FeedbackInsertPayload = z.object({
  posted_at: z.string().nullable().optional(),
  reply_to_external_id: z.string().nullable().optional(),
  thread_id: z.string().nullable().optional(),
  external_id: z.string().nullable().optional(),
  signal_flags: z.unknown().optional(),
});

const ReactionInsertPayload = z.object({
  observed_at: z.string().optional(),
  feedback_id: z.string().optional(),
});

const PollVoteInsertPayload = z.object({
  observed_at: z.string().optional(),
  poll_id: z.string().optional(),
});

const PollUpdatePayload = z.object({
  updated_at: z.string().optional(),
  feedback_id: z.string().optional(),
});

const FormSubmissionRowSchema = z.object({
  submitted_at: z.string(),
});

const FormSubmissionInsertPayload = z.object({
  submitted_at: z.string().optional(),
  game_id: z.string().optional(),
});

function isActivityPost(
  payload: z.infer<typeof FeedbackInsertPayload>,
): boolean {
  if (
    payload.reply_to_external_id !== undefined &&
    payload.reply_to_external_id !== null
  ) {
    return true;
  }
  // Thread follow-ups: a message inside a thread that isn't the
  // thread-creating head. The head's external_id equals its thread_id
  // (Discord's snowflake convention); subsequent posts in the same
  // thread don't.
  if (
    payload.thread_id !== undefined &&
    payload.thread_id !== null &&
    payload.external_id !== payload.thread_id
  ) {
    return true;
  }
  // Poll-bearing messages count as activity even when standalone -
  // the polls feed lists them and the activity panel surfaces a
  // "created poll" entry for the same row.
  const flags = payload.signal_flags;
  if (flags !== null && flags !== undefined && typeof flags === 'object') {
    const hasPoll = (flags as Record<string, unknown>).hasPoll;
    if (hasPoll === true) return true;
  }
  return false;
}

export function useFeedbackUnreadFlag(
  gameId: string | null,
  lastReadAt: string | null,
): boolean {
  const [latestActivityAt, setLatestActivityAt] = useState<string | null>(null);

  // Initial fetch: take the max of the latest reaction and the latest
  // reply/thread-follow-up post in this game.
  useEffect(() => {
    if (gameId === null) {
      setLatestActivityAt(null);
      return;
    }
    let cancelled = false;
    setLatestActivityAt(null);

    const reactionsP = supabase
      .from('feedback_reaction')
      // !inner forces a join filter; rows whose feedback_pool.game_id
      // doesn't match are dropped. Cheap because feedback_reaction has
      // an index on feedback_id, and feedback_pool.game_id is indexed.
      .select('observed_at, feedback_pool!inner(game_id)')
      .eq('feedback_pool.game_id', gameId)
      .order('observed_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();

    // Replies live in feedback_pool with reply_to_external_id IS NOT
    // NULL. Thread follow-ups also count but require a column-to-
    // column comparison (external_id != thread_id) that postgrest
    // .or() doesn't support; we cover those via realtime instead.
    // The initial-load gap is small - the first reaction or reply
    // event after the user opens the app fixes it.
    const repliesP = supabase
      .from('feedback_pool')
      .select('posted_at')
      .eq('game_id', gameId)
      .not('reply_to_external_id', 'is', null)
      .order('posted_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();

    // Poll-bearing messages: most-recent posted_at among feedback_pool
    // rows for this game where signal_flags.hasPoll is true.
    const pollPostsP = supabase
      .from('feedback_pool')
      .select('posted_at')
      .eq('game_id', gameId)
      .eq('signal_flags->>hasPoll', 'true')
      .order('posted_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();

    // Latest poll vote for this game's polls.
    // Migration 0091 added feedback_pool.poll_id which creates a second
    // FK between the two tables; explicit !feedback_id pins the embed
    // to the original "poll attached to this message" relationship.
    const pollVotesP = supabase
      .from('feedback_poll_vote')
      .select('observed_at, feedback_poll!inner(feedback_pool!feedback_id!inner(game_id))')
      .eq('feedback_poll.feedback_pool.game_id', gameId)
      .order('observed_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();

    // Latest poll-state update (covers finalisation flips and the
    // bot's MessageUpdate vote-tally refresh).
    const pollUpdatesP = supabase
      .from('feedback_poll')
      .select('updated_at, feedback_pool!feedback_id!inner(game_id)')
      .eq('feedback_pool.game_id', gameId)
      .order('updated_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();

    // Latest form submission for this game.
    const formSubmissionsP = supabase
      .from('feedback_form_submissions')
      .select('submitted_at')
      .eq('game_id', gameId)
      .order('submitted_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();

    void Promise.all([
      reactionsP,
      repliesP,
      pollPostsP,
      pollVotesP,
      pollUpdatesP,
      formSubmissionsP,
    ]).then(([reactionRes, replyRes, pollPostRes, pollVoteRes, pollUpdateRes, formSubmissionRes]) => {
      if (cancelled) return;
      let max: string | null = null;
      const bump = (ts: string | null | undefined): void => {
        if (ts === null || ts === undefined) return;
        if (max === null || ts > max) max = ts;
      };
      if (reactionRes.data !== null) {
        const r = ReactionRowSchema.safeParse(reactionRes.data);
        if (r.success) bump(r.data.observed_at);
      }
      if (replyRes.data !== null) {
        const r = PostRowSchema.safeParse(replyRes.data);
        if (r.success) bump(r.data.posted_at);
      }
      if (pollPostRes.data !== null) {
        const r = PostRowSchema.safeParse(pollPostRes.data);
        if (r.success) bump(r.data.posted_at);
      }
      if (pollVoteRes.data !== null) {
        const r = ReactionRowSchema.safeParse(pollVoteRes.data);
        if (r.success) bump(r.data.observed_at);
      }
      if (pollUpdateRes.data !== null) {
        const r = z
          .object({ updated_at: z.string() })
          .safeParse(pollUpdateRes.data);
        if (r.success) bump(r.data.updated_at);
      }
      if (formSubmissionRes.data !== null) {
        const r = FormSubmissionRowSchema.safeParse(formSubmissionRes.data);
        if (r.success) bump(r.data.submitted_at);
      }
      setLatestActivityAt(max);
    });
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  // Realtime: bump latestActivityAt on relevant events.
  useEffect(() => {
    if (gameId === null) return;
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectDelay = 1_000;
    let lastStatus: string | null = null;

    function teardownChannel(): void {
      if (channel !== null) {
        // See use-feedback-inbox-read.ts for why we use removeChannel.
        void supabase.removeChannel(channel);
        channel = null;
      }
    }

    function bumpFromTimestamp(ts: string | null | undefined): void {
      if (ts === null || ts === undefined) return;
      setLatestActivityAt((current) => {
        if (current === null) return ts;
        return ts > current ? ts : current;
      });
    }

    function onFeedbackInsert(rowNew: unknown): void {
      const parsed = FeedbackInsertPayload.safeParse(rowNew);
      if (!parsed.success) return;
      if (!isActivityPost(parsed.data)) return;
      bumpFromTimestamp(parsed.data.posted_at);
    }

    async function onReactionInsert(rowNew: unknown): Promise<void> {
      const parsed = ReactionInsertPayload.safeParse(rowNew);
      if (!parsed.success || parsed.data.feedback_id === undefined) return;
      // Reactions don't carry game_id; verify the reacted-to row
      // belongs to this game before bumping. Cheap PK lookup.
      const { data } = await supabase
        .from('feedback_pool')
        .select('id')
        .eq('id', parsed.data.feedback_id)
        .eq('game_id', gameId)
        .maybeSingle();
      if (cancelled || data === null) return;
      bumpFromTimestamp(parsed.data.observed_at ?? null);
    }

    async function onPollVoteInsert(rowNew: unknown): Promise<void> {
      const parsed = PollVoteInsertPayload.safeParse(rowNew);
      if (!parsed.success || parsed.data.poll_id === undefined) return;
      // Vote payload carries poll_id; resolve to game via inner
      // join so we only bump for our scope.
      const { data } = await supabase
        .from('feedback_poll')
        .select('id, feedback_pool!feedback_id!inner(game_id)')
        .eq('id', parsed.data.poll_id)
        .eq('feedback_pool.game_id', gameId)
        .maybeSingle();
      if (cancelled || data === null) return;
      bumpFromTimestamp(parsed.data.observed_at ?? null);
    }

    async function onPollUpdate(rowNew: unknown): Promise<void> {
      const parsed = PollUpdatePayload.safeParse(rowNew);
      if (!parsed.success || parsed.data.feedback_id === undefined) return;
      const { data } = await supabase
        .from('feedback_pool')
        .select('id')
        .eq('id', parsed.data.feedback_id)
        .eq('game_id', gameId)
        .maybeSingle();
      if (cancelled || data === null) return;
      bumpFromTimestamp(parsed.data.updated_at ?? null);
    }

    function onFormSubmissionInsert(rowNew: unknown): void {
      // Subscription is server-filtered by game_id, so any payload that
      // lands here is in scope. No extra DB lookup needed.
      const parsed = FormSubmissionInsertPayload.safeParse(rowNew);
      if (!parsed.success) return;
      bumpFromTimestamp(parsed.data.submitted_at ?? null);
    }

    function connect(): void {
      if (cancelled) return;
      teardownChannel();
      const next = supabase
        .channel(`feedback-unread-flag:${gameId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'feedback_pool',
            filter: `game_id=eq.${gameId}`,
          },
          (payload) => {
            onFeedbackInsert(payload.new);
          },
        )
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'feedback_reaction' },
          (payload) => {
            void onReactionInsert(payload.new);
          },
        )
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'feedback_poll_vote' },
          (payload) => {
            void onPollVoteInsert(payload.new);
          },
        )
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'feedback_poll' },
          (payload) => {
            void onPollUpdate(payload.new);
          },
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'feedback_form_submissions',
            filter: `game_id=eq.${gameId}`,
          },
          (payload) => {
            onFormSubmissionInsert(payload.new);
          },
        );
      channel = next;
      next.subscribe((status) => {
        if (cancelled) return;
        lastStatus = status;
        if (status === 'SUBSCRIBED') {
          reconnectDelay = 1_000;
          if (reconnectTimer !== null) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
          return;
        }
        if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          if (reconnectTimer !== null) return;
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
            connect();
          }, reconnectDelay);
        }
      });
    }

    function onVisibility(): void {
      if (cancelled) return;
      if (document.visibilityState !== 'visible') return;
      if (lastStatus !== 'SUBSCRIBED') {
        if (reconnectTimer !== null) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        reconnectDelay = 1_000;
        connect();
      }
    }

    document.addEventListener('visibilitychange', onVisibility);
    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      teardownChannel();
    };
  }, [gameId]);

  if (gameId === null) return false;
  if (latestActivityAt === null) return false;
  if (lastReadAt === null) return false;
  return latestActivityAt > lastReadAt;
}
